import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  AdminRole,
  AdminScreen,
  AdminUserWithRoles,
  CreateAdminRequest,
  SaveAdminRoleRequest,
  UpdateAdminAssignmentsRequest,
} from '../types/admin-roles';
import RolesTab from '../components/admin-roles/RolesTab';
import AdminsTab from '../components/admin-roles/AdminsTab';

type Tab = 'roles' | 'admins';

/**
 * Spec 050 — super-admin console for screen access.
 *
 * Two tabs, because the two things are genuinely separate: Roles defines what
 * bundles of screens exist, Admins decides who holds them.
 */
export default function AdminRolesPage() {
  const { token, user, isSuperAdmin } = useAuth();

  const [tab, setTab] = useState<Tab>('roles');
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [screens, setScreens] = useState<AdminScreen[]>([]);
  const [admins, setAdmins] = useState<AdminUserWithRoles[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRolesAndScreens = useCallback(async () => {
    if (!token) return;
    const [roleList, screenList] = await Promise.all([
      api.listAdminRoles(token),
      api.listAdminScreens(token),
    ]);
    setRoles(roleList);
    setScreens(screenList);
  }, [token]);

  const loadAdmins = useCallback(async () => {
    if (!token) return;
    setAdmins(await api.listAdminsWithRoles(token, search || undefined));
  }, [token, search]);

  useEffect(() => {
    if (!token || !isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([loadRolesAndScreens(), loadAdmins()]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load admin roles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isSuperAdmin, loadRolesAndScreens, loadAdmins]);

  const createRole = async (body: SaveAdminRoleRequest) => {
    await api.createAdminRole(body, token!);
    await loadRolesAndScreens();
  };

  const updateRole = async (roleId: string, body: SaveAdminRoleRequest) => {
    await api.updateAdminRole(roleId, body, token!);
    // Screen sets changed, so any admin holding this role has different access.
    await Promise.all([loadRolesAndScreens(), loadAdmins()]);
  };

  const deleteRole = async (roleId: string) => {
    await api.deleteAdminRole(roleId, token!);
    await loadRolesAndScreens();
  };

  const createAdmin = async (body: CreateAdminRequest) => {
    await api.createAdmin(body, token!);
    await Promise.all([loadAdmins(), loadRolesAndScreens()]);
  };

  const updateAssignments = async (subjectId: string, body: UpdateAdminAssignmentsRequest) => {
    await api.updateAdminAssignments(subjectId, body, token!);
    await Promise.all([loadAdmins(), loadRolesAndScreens()]);
  };

  const deleteAdmin = async (subjectId: string) => {
    await api.deleteAdmin(subjectId, token!);
    await Promise.all([loadAdmins(), loadRolesAndScreens()]);
  };

  const sendPasswordReset = async (subjectId: string) => {
    await api.sendAdminPasswordReset(subjectId, token!);
  };

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-page admin-roles-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Admin Roles</h1>
        <p className="admin-page-subtitle">
          Define which screens each admin can open. Changes take effect within a minute.
        </p>
      </div>

      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'roles'}
          className={`admin-tab${tab === 'roles' ? ' active' : ''}`}
          onClick={() => setTab('roles')}
        >
          Roles
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'admins'}
          className={`admin-tab${tab === 'admins' ? ' active' : ''}`}
          onClick={() => setTab('admins')}
        >
          Admin Users
        </button>
      </div>

      {loading && <div className="admin-loading">Loading…</div>}
      {error && !loading && <div className="admin-error">{error}</div>}

      {!loading && !error && tab === 'roles' && (
        <RolesTab
          roles={roles}
          screens={screens}
          onCreate={createRole}
          onUpdate={updateRole}
          onDelete={deleteRole}
        />
      )}

      {!loading && !error && tab === 'admins' && (
        <AdminsTab
          admins={admins}
          roles={roles}
          currentUserId={user?.id}
          search={search}
          onSearchChange={setSearch}
          onCreate={createAdmin}
          onUpdate={updateAssignments}
          onDelete={deleteAdmin}
          onResetPassword={sendPasswordReset}
        />
      )}
    </div>
  );
}
